import {
  Timeline as ReuiTimeline,
  TimelineContent,
  TimelineDate,
  TimelineIndicator,
  TimelineItem,
  TimelineSeparator,
} from "@repo/ui/components/reui/timeline";
import { cn } from "@repo/ui/lib/utils";
import type { ComponentProps, CSSProperties, ReactNode } from "react";
import { useTimeline } from "./context";

// The shared indicator defaults are sized for a size-4 dot; ours is size-2, so the
// connector needs its own offset to leave an even gap above and below each dot.
const SEPARATOR =
  "bg-border group-data-[orientation=vertical]/timeline:w-px group-data-[orientation=vertical]/timeline:translate-y-3 group-data-[orientation=vertical]/timeline:h-[calc(100%-0.75rem)]";

/** The outer landmark. The card border is the caller's, so a panel can omit it. */
function Frame({
  label = "Visit timeline",
  className,
  children,
}: {
  label?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section aria-label={label} className={className}>
      {children}
    </section>
  );
}

function Header({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("flex flex-wrap items-baseline justify-between gap-2", className)}>{children}</div>;
}

/**
 * The frame's commit bar, held below the track rather than after it. `sticky`
 * would not do: the frame clips, so it is its own scrollport and a sticky child
 * has nothing to stick against. The frame caps its height and scrolls the track
 * instead, which leaves this a plain flex sibling that cannot scroll away.
 */
function Footer({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("shrink-0", className)}>{children}</div>;
}

function Title({ className, children }: { className?: string; children: ReactNode }) {
  return <h2 className={cn("font-medium", className)}>{children}</h2>;
}

function Summary({ className, children }: { className?: string; children: ReactNode }) {
  return <p className={cn("text-muted-foreground", className)}>{children}</p>;
}

/**
 * `trackStyle` sizes the AXIS, not the padding wrapper: every step is absolutely
 * positioned inside it, so without an explicit height the track would collapse.
 */
function Track({
  className,
  trackStyle,
  children,
}: {
  className?: string;
  trackStyle?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <div className={cn("space-y-3", className)}>
      {/* Value 0: one-based items never receive data-completed, so nothing reads as progress. */}
      <ReuiTimeline orientation="vertical" value={0} className="relative" style={trackStyle}>
        {children}
      </ReuiTimeline>
    </div>
  );
}

interface StepProps extends Omit<ComponentProps<typeof TimelineItem>, "step"> {
  index: number;
}

function Step({ index, className, children, ...props }: StepProps) {
  return (
    <TimelineItem step={index} className={cn("min-w-0 flex-none", className)} {...props}>
      {children}
    </TimelineItem>
  );
}

function Gutter({ className, children }: { className?: string; children: ReactNode }) {
  return <TimelineDate className={cn("mb-0 tabular-nums", className)}>{children}</TimelineDate>;
}

/** The dot plus the connector running down to the next item. */
function Marker({ className }: { className?: string }) {
  return (
    <>
      <TimelineIndicator className={cn("size-2", className)} />
      <TimelineSeparator className={SEPARATOR} />
    </>
  );
}

/** The terminus: a dot with nothing below it, so it takes no connector. */
function EndMarker({ className }: { className?: string }) {
  return <TimelineIndicator className={cn("size-2", className)} />;
}

function Content({ className, children, ...props }: ComponentProps<typeof TimelineContent>) {
  return (
    <TimelineContent className={cn("min-w-0", className)} {...props}>
      {children}
    </TimelineContent>
  );
}

/**
 * Envelope time the plan books but does not fill. `fill` takes the step's whole
 * extent instead of the scaled height — the panel's steps already own their
 * height, and scaling twice would overflow them.
 */
function Unallocated({ minutes, fill }: { minutes: number; fill?: boolean }) {
  const { meta } = useTimeline();
  return (
    <div
      className="flex items-center justify-center rounded-md border border-dashed text-muted-foreground text-xs"
      style={{ height: fill ? "100%" : meta.scale(minutes) }}
    >
      {minutes} min unallocated
    </div>
  );
}

export const Timeline = {
  Frame,
  Header,
  Footer,
  Title,
  Summary,
  Track,
  Step,
  Gutter,
  Marker,
  EndMarker,
  Content,
  Unallocated,
};
